import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
} from "@mui/material";
import DownloadRegistrationPdfButton from "../../../components/download/DownloadRegistrationPdfButton";
import { displayDate, formatIBAN } from "../../../utils/dataUtils";

const getContractName = (contract) =>
  `${contract?.title || ""} ${contract?.firstName || ""} ${
    contract?.lastName || ""
  }`.trim() || "—";

export default function ContractDetailsDialog({
  open,
  contract,
  members,
  membersLoading,
  membersError,
  membersErrorMessage,
  onClose,
}) {
  if (!contract) return null;

  const membersCount = members.length;
  const isPublic = contract?.publicRegistrationMeta?.createdViaPublic;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5">Vertragsdetails</Typography>
            <Typography variant="body2" color="text.secondary">
              {getContractName(contract)}
            </Typography>
          </Box>
          {isPublic && <Chip label="Öffentlich" color="info" size="small" />}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {membersLoading && <LinearProgress />}
          {membersError && (
            <Alert severity="warning">
              {membersErrorMessage ||
                "Mitgliedsdaten konnten nicht geladen werden."}
            </Alert>
          )}

          {contract?.publicRegistrationMeta?.createdViaPublic &&
            contract?.registrationConfirmationPdf?.relativePath && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Öffentliche Anmeldung
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Dieser Vertrag wurde über eine öffentliche Registrierung erstellt.
                </Typography>
                <DownloadRegistrationPdfButton
                  contractId={contract._id}
                  contract={contract}
                  variant="button"
                  size="medium"
                />
              </Box>
            )}

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Vertragsperson
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Anrede
                </Typography>
                <Typography>{contract?.title || "—"}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Vorname
                </Typography>
                <Typography>{contract?.firstName || "—"}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Nachname
                </Typography>
                <Typography>{contract?.lastName || "—"}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Geburtstag
                </Typography>
                <Typography>{displayDate(contract?.birthdate)}</Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="body2" color="text.secondary">
                  Kontoinhaber
                </Typography>
                <Typography>{contract?.owner || "—"}</Typography>
                {contract?.isOwner === false && (
                  <Typography variant="caption" color="text.secondary">
                    Abweichender Zahler
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Kontakt
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  E-Mail
                </Typography>
                <Typography>{contract?.email || "—"}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Telefon
                </Typography>
                <Typography>{contract?.phoneNumber || "—"}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Mobil
                </Typography>
                <Typography>{contract?.mobile || "—"}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Straße, Hausnummer
                </Typography>
                <Typography>{contract?.street || "—"}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  PLZ
                </Typography>
                <Typography>{contract?.zip || "—"}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Ort
                </Typography>
                <Typography>{contract?.city || "—"}</Typography>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Bankdaten
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Kreditinstitut
                </Typography>
                <Typography>{contract?.bank || "—"}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  IBAN
                </Typography>
                <Typography>{formatIBAN(contract?.iban || "") || "—"}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  BIC
                </Typography>
                <Typography>{contract?.bic || "—"}</Typography>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Zuordnung & Übersicht
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Standort
                </Typography>
                <Typography>{contract?.standortRef?.siteName || "—"}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Mitglieder
                </Typography>
                <Typography>{membersCount}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Erstellt am
                </Typography>
                <Typography>{displayDate(contract?.createdAt)}</Typography>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Mitglieder im Vertrag
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {membersCount === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Keine Mitglieder zugeordnet.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Mitglied</TableCell>
                      <TableCell>Mitglieds-Nr.</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Vertragsdatum</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.map((member) => (
                      <TableRow key={member._id}>
                        <TableCell>
                          {`${member?.title || ""} ${member?.firstName || ""} ${
                            member?.lastName || ""
                          }`.trim() || "—"}
                        </TableCell>
                        <TableCell>{member?.memberNumber || "—"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={member?.status || "—"}
                            color={
                              member?.status === "active"
                                ? "success"
                                : member?.status === "terminated"
                                  ? "error"
                                  : "default"
                            }
                          />
                        </TableCell>
                        <TableCell>{displayDate(member?.contractDate)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ContractDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  contract: PropTypes.object,
  members: PropTypes.array,
  membersLoading: PropTypes.bool,
  membersError: PropTypes.bool,
  membersErrorMessage: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

ContractDetailsDialog.defaultProps = {
  contract: null,
  members: [],
  membersLoading: false,
  membersError: false,
  membersErrorMessage: "",
};
